/**
 * W4-MAP: Campaign map DOM renderer.
 *
 * Renders a Slay-Spire-style node graph using SVG overlaid on a grid of DOM nodes.
 * Nodes are rendered as circles (boss = hexagon) with connection lines.
 * State: visited (filled), available (dashed pulse), locked (grey).
 */

import {
  generateChapter,
  availableNodes,
  type CampaignChapter,
  type CampaignNode,
  type NodeKind,
} from '../shared/campaign';
import { loadProgress, saveProgress } from '../shared/progress';

export interface CampaignMapActions {
  onEnterBattle: (nodeId: string, kind: CampaignNode['kind']) => void;
  onBack: () => void;
}

// ---------------------------------------------------------------------------
// Node visual config
// ---------------------------------------------------------------------------
const KIND_EMOJI: Record<NodeKind, string> = {
  normal:  '⚔',
  elite:   '💀',
  rest:    '💊',
  event:   '❓',
  mystery: '🌀',
  boss:    '💥',
};

const KIND_LABEL: Record<NodeKind, string> = {
  normal:  '普通',
  elite:   '精英',
  rest:    '補給',
  event:   '事件',
  mystery: '神秘',
  boss:    'BOSS',
};

const KIND_COLOR: Record<NodeKind, string> = {
  normal:  '#1976d2',
  elite:   '#ad1457',
  rest:    '#00897b',
  event:   '#f57c00',
  mystery: '#6a1b9a',
  boss:    '#c62828',
};

// ---------------------------------------------------------------------------
// Main render function
// ---------------------------------------------------------------------------
export function renderCampaignMap(
  container: HTMLElement,
  actions: CampaignMapActions,
): void {
  const progress = loadProgress();
  if (!progress) {
    container.innerHTML = '<p style="color:#ff8">進度讀取失敗</p>';
    return;
  }

  const { chapter, visitedNodes, graphSeed } = progress.campaignState;
  const chapterIdx = (chapter >= 1 && chapter <= 5 ? chapter : 1) as 1|2|3|4|5;
  const chapterData = generateChapter(chapterIdx, graphSeed);
  const available = new Set(availableNodes(chapterData, visitedNodes).map((n) => n.id));
  const visited = new Set(visitedNodes);

  // Root wrapper
  const root = document.createElement('div');
  root.className = 'campaign-map';
  root.innerHTML = `
    <div class="cm-header">
      <button class="cm-back-btn">← 返回</button>
      <h2>⚔ 戰役 — 第 ${chapterIdx} 章</h2>
      <span class="cm-chapter-info">${chapterData.nodes.length} 個節點</span>
    </div>
    <div class="cm-body">
      <div class="cm-graph-wrapper" id="cm-graph"></div>
    </div>
  `;
  container.appendChild(root);

  root.querySelector('.cm-back-btn')!.addEventListener('click', () => {
    root.remove();
    actions.onBack();
  });

  const graphWrapper = root.querySelector('#cm-graph') as HTMLElement;
  buildGraph(graphWrapper, chapterData, visited, available, (nodeId, kind) => {
    // Confirm modal before battle
    showConfirmModal(root, nodeId, kind, () => {
      // Update progress: set currentNodeId
      const p = loadProgress();
      if (p) {
        p.campaignState.currentNodeId = nodeId;
        saveProgress(p);
      }
      root.remove();
      actions.onEnterBattle(nodeId, kind);
    });
  });
}

// ---------------------------------------------------------------------------
// Build SVG + node grid
// ---------------------------------------------------------------------------
const NODE_R = 26;      // circle radius px
const COL_W = 80;       // horizontal spacing px
const ROW_H = 70;       // vertical spacing px
const PAD_X = 48;       // left/right padding
const PAD_Y = 32;       // top/bottom padding
const COLS = 4;
const ROWS = 10;

function nodeX(col: number): number {
  // Cols 0-3 evenly distributed; boss uses centre (col 0 → col 1.5 effectively)
  return PAD_X + col * COL_W + COL_W / 2;
}

function nodeY(row: number): number {
  // Row 0 is bottom, row 9 (boss) is top — render top-to-bottom means row 0 is low
  // Slay-Spire style: boss at top visually
  return PAD_Y + (ROWS - 1 - row) * ROW_H;
}

function buildGraph(
  wrapper: HTMLElement,
  chapter: CampaignChapter,
  visited: Set<string>,
  available: Set<string>,
  onNodeClick: (nodeId: string, kind: NodeKind) => void,
): void {
  const svgW = PAD_X * 2 + COLS * COL_W;
  const svgH = PAD_Y * 2 + (ROWS - 1) * ROW_H;

  const nodeMap = new Map(chapter.nodes.map((n) => [n.id, n]));

  // Create SVG for edges
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('width', String(svgW));
  svg.setAttribute('height', String(svgH));
  svg.setAttribute('class', 'cm-svg');

  // Draw edges first (behind nodes)
  for (const node of chapter.nodes) {
    for (const edgeId of node.edges) {
      const target = nodeMap.get(edgeId);
      if (!target) continue;

      const x1 = node.kind === 'boss' ? svgW / 2 : nodeX(node.position.col);
      const y1 = node.kind === 'boss' ? nodeY(node.position.row) : nodeY(node.position.row);
      const x2 = target.kind === 'boss' ? svgW / 2 : nodeX(target.position.col);
      const y2 = nodeY(target.position.row);

      const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
      line.setAttribute('x1', String(x1));
      line.setAttribute('y1', String(y1));
      line.setAttribute('x2', String(x2));
      line.setAttribute('y2', String(y2));

      const fromVisited = visited.has(node.id);
      const toVisited = visited.has(edgeId);
      if (fromVisited && toVisited) {
        line.setAttribute('stroke', '#80cbc4');
        line.setAttribute('stroke-width', '2');
      } else if (fromVisited) {
        line.setAttribute('stroke', '#4a6070');
        line.setAttribute('stroke-width', '1.5');
        line.setAttribute('stroke-dasharray', '4 4');
      } else {
        line.setAttribute('stroke', '#2a3f50');
        line.setAttribute('stroke-width', '1');
      }
      svg.appendChild(line);
    }
  }

  wrapper.appendChild(svg);

  // Create node DOM elements (absolutely positioned over SVG)
  const nodesLayer = document.createElement('div');
  nodesLayer.className = 'cm-nodes-layer';
  nodesLayer.style.cssText = `position:absolute;top:0;left:0;width:${svgW}px;height:${svgH}px;`;

  for (const node of chapter.nodes) {
    const cx = node.kind === 'boss' ? svgW / 2 : nodeX(node.position.col);
    const cy = nodeY(node.position.row);

    const isVisited = visited.has(node.id);
    const isAvailable = available.has(node.id);
    const isBoss = node.kind === 'boss';

    const el = document.createElement('div');
    el.className = 'cm-node';
    if (isVisited)   el.classList.add('cm-node--visited');
    if (isAvailable) el.classList.add('cm-node--available');
    if (!isVisited && !isAvailable) el.classList.add('cm-node--locked');
    if (isBoss)      el.classList.add('cm-node--boss');

    const size = isBoss ? NODE_R * 2.2 : NODE_R * 2;
    el.style.cssText = `
      position:absolute;
      left:${cx - size / 2}px;
      top:${cy - size / 2}px;
      width:${size}px;
      height:${size}px;
      background:${isVisited ? KIND_COLOR[node.kind] : isAvailable ? KIND_COLOR[node.kind] + '44' : '#1a2730'};
      border:2px solid ${isAvailable ? KIND_COLOR[node.kind] : isVisited ? KIND_COLOR[node.kind] : '#2a3f50'};
      border-radius:${isBoss ? '6px' : '50%'};
      display:flex;flex-direction:column;align-items:center;justify-content:center;
      cursor:${isAvailable ? 'pointer' : 'default'};
      font-size:${isBoss ? 18 : 14}px;
      user-select:none;
      transition:transform 0.1s;
      ${isAvailable ? 'box-shadow:0 0 10px ' + KIND_COLOR[node.kind] + '88;' : ''}
    `;

    el.innerHTML = `
      <span style="font-size:${isBoss ? 20 : 16}px">${KIND_EMOJI[node.kind]}</span>
      <span style="font-size:9px;color:#ccc;margin-top:2px">${KIND_LABEL[node.kind]}</span>
    `;

    if (isAvailable) {
      el.title = `進入：${KIND_LABEL[node.kind]}`;
      el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.15)'; });
      el.addEventListener('mouseleave', () => { el.style.transform = ''; });
      el.addEventListener('click', () => onNodeClick(node.id, node.kind));
    }

    nodesLayer.appendChild(el);
  }

  const wrapperContainer = document.createElement('div');
  wrapperContainer.style.cssText = `position:relative;width:${svgW}px;height:${svgH}px;`;
  wrapperContainer.appendChild(svg);
  wrapperContainer.appendChild(nodesLayer);

  // Replace SVG alone
  wrapper.innerHTML = '';
  wrapper.appendChild(wrapperContainer);
}

// ---------------------------------------------------------------------------
// Confirm modal
// ---------------------------------------------------------------------------
function showConfirmModal(
  parent: HTMLElement,
  _nodeId: string,
  kind: NodeKind,
  onConfirm: () => void,
): void {
  const overlay = document.createElement('div');
  overlay.className = 'cm-modal-overlay';
  overlay.innerHTML = `
    <div class="cm-modal">
      <div class="cm-modal-icon">${KIND_EMOJI[kind]}</div>
      <h3>${KIND_LABEL[kind]}</h3>
      <p class="cm-modal-desc">${kindDescription(kind)}</p>
      <div class="cm-modal-btns">
        <button class="primary-btn" id="cm-confirm">進入！</button>
        <button class="secondary-btn" id="cm-cancel">取消</button>
      </div>
    </div>
  `;
  parent.appendChild(overlay);

  overlay.querySelector('#cm-confirm')!.addEventListener('click', () => {
    overlay.remove();
    onConfirm();
  });
  overlay.querySelector('#cm-cancel')!.addEventListener('click', () => {
    overlay.remove();
  });
}

function kindDescription(kind: NodeKind): string {
  switch (kind) {
    case 'normal':  return '迎戰 3 波敵軍！';
    case 'elite':   return '強力精英怪等你挑戰，擊敗獲得豐厚獎勵！';
    case 'rest':    return '補滿血量，下次戰鬥額外 +30% 修煉值！';
    case 'event':   return '神秘事件正在等待……';
    case 'mystery': return '未知遭遇，進入才知！';
    case 'boss':    return '章節 BOSS！全力以赴！';
  }
}
